/***
 *
 *   PEP/SANCTIONS RECORD HELPERS
 *   Pure, defensive formatting for Dilisense `checkIndividual` found_records.
 *   No React here — kept separate so all normalization/guards are unit-testable
 *   and shared by the on-screen cards (sanctions-results.js) and the PDF
 *   formatter (pdf-report.js).
 *
 *   The Dilisense API omits empty attributes, and in practice values can arrive
 *   malformed (a scalar where an array is expected, objects inside arrays,
 *   `list_date` as the string "0", unknown enum values). Every helper here
 *   tolerates missing / malformed input without throwing.
 *
 **********/

function safeJson(value) {
  try {
    return JSON.stringify(value);
  } catch (e) {
    return "";
  }
}

/**
 * Coerce any value into a clean array of non-empty strings.
 * null/undefined -> []; scalar -> [scalar]; arrays (incl. nested) flattened;
 * objects reduced via name/title/text then JSON; whitespace trimmed, empties dropped.
 */
export function normalizeToList(value) {
  if (value == null) return [];
  const items = Array.isArray(value) ? value : [value];
  const out = [];

  items.forEach((item) => {
    if (item == null) return;

    if (Array.isArray(item)) {
      normalizeToList(item).forEach((v) => out.push(v));
      return;
    }

    if (typeof item === "object") {
      const label = item.name || item.title || item.text;
      const resolved = label != null ? String(label) : safeJson(item);
      const trimmed = resolved.trim();
      if (trimmed) out.push(trimmed);
      return;
    }

    const trimmed = String(item).trim();
    if (trimmed) out.push(trimmed);
  });

  return out;
}

/** Join a (possibly malformed) value into a single display string. */
export function toDisplay(value) {
  return normalizeToList(value).join(", ");
}

/** "STATE_OWNED_ENTERPRISE" -> "State Owned Enterprise". Null-safe. */
export function humanizeEnum(value) {
  if (value == null) return "";
  const str = String(value).trim();
  if (!str) return "";
  return str
    .toLowerCase()
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

/**
 * Dilisense `list_date` is a unix timestamp in milliseconds, often "0" when the
 * source provides no date. Returns "" for 0 / falsy / non-numeric input.
 */
export function formatListDate(value) {
  if (value == null) return "";
  const ms = Number(value);
  if (!Number.isFinite(ms) || ms <= 0) return "";
  const date = new Date(ms);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString();
}

export const SOURCE_TYPE_LABELS = {
  SANCTION: "Sanction",
  PEP: "PEP",
  CRIMINAL: "Criminal",
  OTHER: "Other",
};

export const PEP_TYPE_LABELS = {
  POLITICIAN: "Politician",
  JUDGE: "Judge",
  BOARD_MEMBER_OF_CENTRAL_BANK: "Board Member of Central Bank",
  EXECUTIVE_AUDITOR: "Executive Auditor",
  ADMINISTRATION_OFFICE_EXECUTIVE: "Administration Office Executive",
  MILITARY_OFFICIAL: "Military Official",
  EMBASSY_OFFICIAL: "Embassy Official",
  INTERNATIONAL_ORGANIZATION_OFFICIAL: "International Organization Official",
  RELATIVES_AND_CLOSE_ASSOCIATES: "Relatives and Close Associates",
  STATE_OWNED_ENTERPRISE: "State Owned Enterprise",
  BOARD_MEMBER_OF_STATE_OWNED_ENTERPRISE:
    "Board Member of State Owned Enterprise",
  PROSECUTION_OFFICIAL: "Prosecution Official",
  AGENCY_OFFICIAL: "Agency Official",
  OTHER: "Other",
};

export const GENDER_LABELS = {
  MALE: "Male",
  FEMALE: "Female",
  UNKNOWN: "Unknown",
};

export const ENTITY_TYPE_LABELS = {
  INDIVIDUAL: "Individual",
  UNKNOWN: "Unknown",
};

export const PEP_LEVEL_LABELS = {
  1: "Level 1 — Top national leadership",
  2: "Level 2 — Senior national officials",
  3: "Level 3 — Sub-national / institutional",
  4: "Level 4 — Local / administrative",
};

/** Look a value up in an enum label map, falling back to humanized text. */
function labelFor(map, value) {
  if (value == null || value === "") return "";
  if (typeof value === "string") {
    const key = value.trim().toUpperCase();
    if (map[key] != null) return map[key];
  } else if (map[value] != null) {
    return map[value];
  }
  return humanizeEnum(value);
}

/** Descriptive PEP level label, tolerant of number or numeric string. */
export function pepLevelLabel(value) {
  if (value == null || value === "") return "";
  const level = Number(value);
  if (!Number.isFinite(level)) return "";
  return PEP_LEVEL_LABELS[level] || `Level ${level}`;
}

function sourceTypeColor(sourceType) {
  switch (sourceType) {
    case "SANCTION":
    case "CRIMINAL":
      return "red";
    case "PEP":
      return "orange";
    default:
      return "blue";
  }
}

function asRecord(record) {
  return record && typeof record === "object" && !Array.isArray(record)
    ? record
    : {};
}

/** Best display name for a record, with a graceful fallback chain. */
export function getRecordTitle(record) {
  const r = asRecord(record);
  const name = toDisplay(r.name);
  if (name) return name;
  const transliterated = toDisplay(r.tl_name);
  if (transliterated) return transliterated;
  const combined = [
    ...normalizeToList(r.given_names),
    ...normalizeToList(r.last_names),
  ]
    .join(" ")
    .trim();
  if (combined) return combined;
  return "Unnamed record";
}

/** Color-coded badges for the card header. Absent fields are skipped. */
export function getRecordBadges(record) {
  const r = asRecord(record);
  const badges = [];

  const sourceType =
    typeof r.source_type === "string" ? r.source_type.trim().toUpperCase() : "";
  if (sourceType) {
    badges.push({
      label: labelFor(SOURCE_TYPE_LABELS, sourceType),
      color: sourceTypeColor(sourceType),
    });
  }

  const pepType = labelFor(PEP_TYPE_LABELS, r.pep_type);
  if (pepType) badges.push({ label: pepType, color: "blue" });

  const level = Number(r.pep_level);
  if (r.pep_level != null && r.pep_level !== "" && Number.isFinite(level)) {
    badges.push({ label: `Level ${level}`, color: "blue" });
  }

  const gender =
    typeof r.gender === "string" ? r.gender.trim().toUpperCase() : "";
  if (gender && gender !== "UNKNOWN") {
    badges.push({ label: labelFor(GENDER_LABELS, gender), color: "blue" });
  }

  return badges;
}

/** Build a { label, value, kind } field, or null when there is no data. */
function field(label, value, kind = "text") {
  const display = toDisplay(value);
  return display ? { label, value: display, kind } : null;
}

/** Links keep their array form so each entry can be rendered individually. */
function linksField(value) {
  const links = normalizeToList(value);
  if (!links.length) return null;
  return { label: "Links", value: links, kind: "links" };
}

/** A compact set of fields for the collapsed card view. */
export function getRecordSummaryFields(record) {
  const r = asRecord(record);
  return [
    field("Date of birth", r.date_of_birth),
    field("Citizenship", r.citizenship),
    field("Source", r.source_id),
  ].filter(Boolean);
}

/**
 * Grouped detail sections for the expanded card view. A section is omitted
 * entirely when none of its fields carry data, mirroring the API's
 * "empty attributes are not present" contract and keeping cards compact.
 */
export function getRecordDetailSections(record) {
  const r = asRecord(record);

  const sections = [
    {
      title: "Identity",
      fields: [
        field("Transliterated name", r.tl_name),
        field("Given names", r.given_names),
        field("Last names", r.last_names),
        field("Alias names", r.alias_names),
        field("Alias given names", r.alias_given_names),
        field("Titles", r.titles),
        field("Functions", r.functions),
        field("Name remarks", r.name_remarks),
      ],
    },
    {
      title: "Birth",
      fields: [
        field("Date of birth", r.date_of_birth),
        field("Date of birth remarks", r.date_of_birth_remarks),
        field("Place of birth", r.place_of_birth),
        field("Place of birth remarks", r.place_of_birth_remarks),
      ],
    },
    {
      title: "Nationality & location",
      fields: [
        field("Citizenship", r.citizenship),
        field("Citizenship remarks", r.citizenship_remarks),
        field("Address", r.address),
        field("Address remarks", r.address_remarks),
        field("Source country", r.source_country),
      ],
    },
    {
      title: "Roles & affiliations",
      fields: [
        field("PEP type", labelFor(PEP_TYPE_LABELS, r.pep_type)),
        field("PEP level", pepLevelLabel(r.pep_level)),
        field("Positions", r.positions),
        field("Occupations", r.occupations),
        field("Political parties", r.political_parties),
      ],
    },
    {
      title: "Relations",
      fields: [
        field("Spouse", r.spouse),
        field("Parents", r.parents),
        field("Children", r.children),
        field("Siblings", r.siblings),
      ],
    },
    {
      title: "Sanction details",
      fields: [
        field("Sanction details", r.sanction_details),
        field("Description", r.description),
        field("Other information", r.other_information),
      ],
    },
    {
      title: "Links",
      fields: [linksField(r.links)],
    },
    {
      title: "Source & metadata",
      fields: [
        field("Source", r.source_id),
        field("Source type", labelFor(SOURCE_TYPE_LABELS, r.source_type)),
        field("Entity type", labelFor(ENTITY_TYPE_LABELS, r.entity_type)),
        field("Listed", formatListDate(r.list_date)),
        field("Record ID", r.id),
      ],
    },
  ];

  return sections
    .map((section) => ({
      title: section.title,
      fields: section.fields.filter(Boolean),
    }))
    .filter((section) => section.fields.length > 0);
}

/**
 * Flat, ordered { label, value } rows covering every field that has data —
 * the single source of truth consumed by the PDF formatter. Always returns at
 * least one row so the PDF never renders an empty record block.
 */
export function getSanctionsRecordFields(record) {
  const r = asRecord(record);
  const title = getRecordTitle(r);
  const rows = [{ label: "Name", value: title }];

  getRecordDetailSections(r).forEach((section) => {
    section.fields.forEach((f) => {
      // "Source" also appears in the metadata section; skip the duplicate.
      if (f.label === "Source" && rows.some((row) => row.label === "Source")) {
        return;
      }
      rows.push({
        label: f.label,
        value: Array.isArray(f.value) ? f.value.join(", ") : f.value,
      });
    });
  });

  return rows;
}

/**
 * Classify a links[] entry. Real URLs become anchor hrefs, bare emails become
 * mailto links, and anything else (e.g. "Facebook: borisjohnson") stays plain
 * text so we never put target="_blank" on a non-URL.
 */
export function classifyLink(value) {
  const str = value == null ? "" : String(value).trim();
  if (!str) return { href: null, label: "" };
  if (/^https?:\/\//i.test(str)) return { href: str, label: str };
  if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(str)) {
    return { href: `mailto:${str}`, label: str };
  }
  return { href: null, label: str };
}
